import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Banner, Button, Input, Text } from '@cloudflare/kumo'
import { ArrowClockwiseIcon } from '@phosphor-icons/react'
import {
  formatPhoneAsYouType,
  individualTaxIdTypesForCountry,
  isValidMobilePhone,
  normalizeTaxId,
  toE164,
  validateTaxId,
} from '@hub-wifi/shared'
import type { EnrollmentStatus } from '@hub-wifi/shared'
import {
  resendEnrollment,
  startEnrollment,
  submitEnrollmentFace,
  verifyEnrollment,
  type StartEnrollmentInput,
} from '../api/enroll'
import { ApiError } from '../api/client'
import { useTranslation } from '../i18n'
import { EnrollDone } from '../components/enroll/EnrollDone'
import { EnrollHeader } from '../components/enroll/EnrollHeader'
import { EnrollProgress } from '../components/enroll/EnrollProgress'
import { PhotoConfirm } from '../components/enroll/PhotoConfirm'
import { PhotoStep } from '../components/enroll/PhotoStep'
import { StickyActionBar } from '../components/enroll/StickyActionBar'
import { DEFAULT_COUNTRY, DEFAULT_TYPE, TaxIdFields } from '../components/enroll/TaxIdFields'
import '../components/enroll/enroll.css'
import {
  applyDigitMask,
  SINGLE_INDIVIDUAL_TAX_ID_TYPE,
  TOGGLEABLE_TAX_ID_TYPE_ID,
} from '../lib/taxIdFields'

interface DetailsForm {
  name: string
  email: string
  mobilePhone: string
  taxIdCountry: string
  taxIdType: string
  taxIdValue: string
  birthDate: string
}

const emptyForm: DetailsForm = {
  name: '',
  email: '',
  mobilePhone: '',
  taxIdCountry: DEFAULT_COUNTRY,
  taxIdType: DEFAULT_TYPE,
  taxIdValue: '',
  birthDate: '',
}

type FormErrors = Partial<Record<keyof DetailsForm, string>>

/**
 * Wizard step state. Step 1 (details) starts the enrollment and mints
 * `{enrollmentId, stepToken}`; step 2 (verify) confirms the emailed 6-digit
 * code; step 3a (photo) captures the selfie (guided camera only) with
 * no other affordance on screen; step 3b (confirm) shows the captured
 * preview, LGPD consent, and the final submit — split out so the "take
 * photo" and "submit" actions are never on the same screen; `done` is the
 * terminal screen, keyed on the real status `submitEnrollmentFace` returns
 * ('approved' vs 'pending-approval' — see enrollmentService.ts#submitFace).
 */
type WizardStep =
  | { kind: 'details' }
  | { kind: 'verify'; enrollmentId: string; stepToken: string }
  | { kind: 'photo'; enrollmentId: string; stepToken: string }
  | { kind: 'confirm'; enrollmentId: string; stepToken: string }
  | { kind: 'done'; status: 'approved' | 'pending-approval' }

/** Verify-step server error codes that mean "this attempt is unrecoverable
 * — start the whole enrollment over" (mirrors ENROLLMENT_STATUS_BY_CODE's
 * 429s for CODE_LOCKED/CODE_SEND_LIMIT in apps/api/src/routes/enroll.ts).
 * CODE_EXPIRED is also a 429 there, but is recoverable via resend, so it is
 * NOT included here. */
/** Emailed verification codes are always 6 digits. */
const CODE_LENGTH = 6

const VERIFY_TERMINAL_CODES = new Set(['CODE_LOCKED', 'CODE_SEND_LIMIT'])

/** Narrows `submitFace`'s `EnrollmentStatus` return to the two terminal
 * values it actually produces (see enrollmentService.ts#submitFace — every
 * other status is either pre-face ('started'/'verified') or post-decision
 * ('rejected'), neither of which `submitFace` returns). Anything unexpected
 * defensively falls to 'pending-approval' rather than crashing the wizard. */
function enrollmentDoneStatus(status: EnrollmentStatus): 'approved' | 'pending-approval' {
  return status === 'approved' ? 'approved' : 'pending-approval'
}

/** Public mobile self-enrollment wizard — route `/enroll/:token`, no auth. */
export function EnrollPage() {
  const { token } = useParams<{ token: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  // The public page has no Layout, so it applies the stored theme itself.
  const [darkMode] = useState(() => {
    try {
      return localStorage.getItem('hub-wifi-dark-mode') === 'true'
    } catch {
      return false
    }
  })
  // False when the company skips the emailed code (drops the verify step).
  const [verifyRequired, setVerifyRequired] = useState(true)

  const [step, setStep] = useState<WizardStep>({ kind: 'details' })
  const [form, setForm] = useState<DetailsForm>(emptyForm)
  const [errors, setErrors] = useState<FormErrors>({})
  const [serverError, setServerError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  function setField<K extends keyof DetailsForm>(key: K, value: DetailsForm[K]) {
    setForm((prev) => {
      const next = { ...prev, [key]: value }
      if (key === 'taxIdCountry') {
        next.taxIdType = individualTaxIdTypesForCountry(value as string)[0]?.id ?? ''
      }
      return next
    })
  }

  /** Live-formats the mobile phone field as the user types. Formatting is
   * skipped while deleting (new value shorter than the current one) — since
   * `AsYouType` re-inserts punctuation from scratch on every call, feeding it
   * a value the user just shortened would fight the caret and can make
   * backspacing a formatted number impossible. Simple appends/replacements
   * still get formatted normally. Also clears a previously-shown
   * `invalidMobile` error as soon as the (new) value becomes a valid mobile
   * number, without requiring another submit. */
  function handlePhoneChange(raw: string) {
    const isDeleting = raw.length < form.mobilePhone.length
    let next = raw
    if (!isDeleting) {
      // `formatPhoneAsYouType` can return '' for pure non-digit garbage
      // (AsYouType strips everything it can't place) — fall back to the raw
      // value so the field isn't silently cleared and validation can still
      // report a proper "invalid" (vs. "required") error on submit.
      next = formatPhoneAsYouType(raw, 'BR') || raw
    }
    setField('mobilePhone', next)
    if (errors.mobilePhone) {
      const e164 = toE164(next, 'BR')
      if (e164 && isValidMobilePhone(e164)) {
        setErrors((prev) => ({ ...prev, mobilePhone: undefined }))
      }
    }
  }

  /** Live-formats the CPF field with its `mask` metadata (`000.000.000-00`)
   * as the user types, same "skip formatting while deleting" simplification
   * as the phone field. The value stored here is display-only — submission
   * still normalizes to digits-only via `normalizeTaxId`, unchanged. */
  function handleCpfChange(raw: string) {
    const mask = SINGLE_INDIVIDUAL_TAX_ID_TYPE?.mask
    const isDeleting = raw.length < form.taxIdValue.length
    const next = !isDeleting && mask ? applyDigitMask(raw, mask) : raw
    setField('taxIdValue', next)
  }

  /** Switches the tax-id field to passport mode — clears the (possibly
   * half-typed) CPF value so it can't be submitted as a passport number.
   * `taxIdCountry` is left as-is (still 'BR', the enrolling company's
   * country) since passport is a fallback within the same enrollment, not a
   * country change. */
  function switchToPassportMode() {
    setForm((prev) => ({ ...prev, taxIdType: TOGGLEABLE_TAX_ID_TYPE_ID, taxIdValue: '' }))
    setErrors((prev) => ({ ...prev, taxIdValue: undefined }))
  }

  /** Inverse of `switchToPassportMode` — back to CPF, clearing the passport
   * value so it can't be submitted as a CPF. */
  function switchToCpfMode() {
    setForm((prev) => ({ ...prev, taxIdType: DEFAULT_TYPE, taxIdValue: '' }))
    setErrors((prev) => ({ ...prev, taxIdValue: undefined }))
  }

  function validate(): FormErrors {
    const next: FormErrors = {}
    if (!form.name.trim()) next.name = t('enroll.validation.required')
    if (!form.email.trim()) next.email = t('enroll.validation.required')
    if (!form.mobilePhone.trim()) {
      next.mobilePhone = t('enroll.validation.required')
    } else {
      const e164 = toE164(form.mobilePhone.trim(), 'BR')
      if (!e164 || !isValidMobilePhone(e164)) {
        next.mobilePhone = t('enroll.validation.invalidMobile')
      }
    }

    const taxIdCheck = validateTaxId(form.taxIdType, form.taxIdValue)
    if (!taxIdCheck.ok) {
      next.taxIdValue = t(`enroll.validation.taxId.${taxIdCheck.reason ?? 'INVALID_FORMAT'}`)
    }

    return next
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!token) return

    const validationErrors = validate()
    setErrors(validationErrors)
    if (Object.keys(validationErrors).length > 0) return

    setServerError(null)
    setSubmitting(true)
    try {
      // `validate()` above already confirmed this parses to a valid mobile —
      // the non-null assertion reflects that guarantee.
      const e164Phone = toE164(form.mobilePhone.trim(), 'BR')!
      const body: StartEnrollmentInput = {
        name: form.name.trim(),
        email: form.email.trim(),
        mobilePhone: e164Phone,
        taxIdCountry: form.taxIdCountry,
        taxIdType: form.taxIdType,
        taxIdValue: normalizeTaxId(form.taxIdType, form.taxIdValue),
        birthDate: form.birthDate.trim() || undefined,
      }
      const result = await startEnrollment(token, body)
      // `!== false` so an older API without the field keeps the verify step.
      setVerifyRequired(result.emailVerificationRequired !== false)
      setStep({
        // `!== false` so an older API without the field keeps the verify step.
        kind: result.emailVerificationRequired !== false ? 'verify' : 'photo',
        enrollmentId: result.enrollmentId,
        stepToken: result.stepToken,
      })
    } catch (err) {
      if (err instanceof ApiError) {
        const code = (err.body as { code?: string } | null)?.code
        // EMAIL_DOMAIN_MISMATCH and COMPANY_TAX_ID_NOT_ALLOWED get dedicated
        // messages — every other `start()` error (INVALID_TOKEN,
        // INVALID_MOBILE, INVALID_TAX_ID, ...) falls back to the generic
        // message.
        if (code === 'EMAIL_DOMAIN_MISMATCH') {
          setServerError(t('enroll.errors.EMAIL_DOMAIN_MISMATCH'))
        } else if (code === 'COMPANY_TAX_ID_NOT_ALLOWED') {
          setServerError(t('enroll.errors.COMPANY_TAX_ID_NOT_ALLOWED'))
        } else {
          setServerError(t('enroll.errors.generic'))
        }
      } else {
        setServerError(t('enroll.errors.generic'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  // --- Step 2 (verify) ---
  const [code, setCode] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [resending, setResending] = useState(false)
  const [verifyError, setVerifyError] = useState<string | null>(null)
  const [verifyTerminal, setVerifyTerminal] = useState(false)

  function resetToStart() {
    setStep({ kind: 'details' })
    setCode('')
    setVerifyError(null)
    setVerifyTerminal(false)
  }

  function handleVerify(e: React.FormEvent) {
    e.preventDefault()
    void submitCode(code)
  }

  /** Digits only; the code length is fixed (6), so submit as soon as it is
   * complete — the Verify button stays for manual/retry. */
  function handleCodeChange(raw: string) {
    const digits = raw.replace(/\D/g, '').slice(0, CODE_LENGTH)
    setCode(digits)
    if (digits.length === CODE_LENGTH && !verifying && !verifyTerminal) void submitCode(digits)
  }

  async function submitCode(value: string) {
    if (step.kind !== 'verify') return

    setVerifyError(null)
    setVerifying(true)
    try {
      await verifyEnrollment(step.enrollmentId, step.stepToken, value)
      setStep({ kind: 'photo', enrollmentId: step.enrollmentId, stepToken: step.stepToken })
    } catch (err) {
      const apiCode =
        err instanceof ApiError ? (err.body as { code?: string } | null)?.code : undefined
      if (apiCode === 'CODE_INCORRECT' || apiCode === 'CODE_EXPIRED' || apiCode === 'CODE_LOCKED') {
        setVerifyError(t(`enroll.step2.errors.${apiCode}`))
      } else {
        setVerifyError(t('enroll.errors.generic'))
      }
      if (apiCode && VERIFY_TERMINAL_CODES.has(apiCode)) {
        setVerifyTerminal(true)
      }
    } finally {
      setVerifying(false)
    }
  }

  async function handleResend() {
    if (step.kind !== 'verify') return

    setVerifyError(null)
    setResending(true)
    try {
      await resendEnrollment(step.enrollmentId, step.stepToken)
      // The old code no longer works; clear it for the new one.
      setCode('')
    } catch (err) {
      const apiCode =
        err instanceof ApiError ? (err.body as { code?: string } | null)?.code : undefined
      if (apiCode === 'CODE_SEND_LIMIT') {
        setVerifyError(t('enroll.step2.errors.CODE_SEND_LIMIT'))
        setVerifyTerminal(true)
      } else {
        setVerifyError(t('enroll.errors.generic'))
      }
    } finally {
      setResending(false)
    }
  }

  // --- Step 3 (face capture + consent) ---

  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null)
  const [photoBase64, setPhotoBase64] = useState<string | null>(null)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const [submittingFace, setSubmittingFace] = useState(false)

  function handleCapture(dataUrl: string) {
    setPhotoError(null)
    setPhotoBase64(dataUrl.split(',')[1] ?? '')
    setPhotoPreviewUrl(dataUrl)
    if (step.kind === 'photo') {
      setStep({ kind: 'confirm', enrollmentId: step.enrollmentId, stepToken: step.stepToken })
    }
  }

  function handleRetake() {
    setPhotoBase64(null)
    setPhotoPreviewUrl(null)
    if (step.kind === 'confirm') {
      setStep({ kind: 'photo', enrollmentId: step.enrollmentId, stepToken: step.stepToken })
    }
  }

  async function handleSubmitFace() {
    if (step.kind !== 'confirm' || !photoBase64) return

    setPhotoError(null)
    setSubmittingFace(true)
    try {
      const result = await submitEnrollmentFace(
        step.enrollmentId,
        step.stepToken,
        photoBase64,
        true,
      )
      setStep({
        kind: 'done',
        status: enrollmentDoneStatus(result.status),
      })
    } catch {
      setPhotoError(t('enroll.errors.generic'))
    } finally {
      setSubmittingFace(false)
    }
  }

  const total = verifyRequired ? 4 : 3
  const stepNumber =
    step.kind === 'details'
      ? 1
      : step.kind === 'verify'
        ? 2
        : step.kind === 'photo'
          ? total - 1
          : total
  const stepName = step.kind === 'done' ? '' : t(`enroll.step.${step.kind}`)

  return (
    <div
      data-mode={darkMode ? 'dark' : 'light'}
      className="min-h-screen bg-kumo-base text-kumo-default"
    >
      <div className="enroll-shell flex flex-col gap-4">
        <EnrollHeader
          darkMode={darkMode}
          title={t('enroll.title')}
          subtitle={t('enroll.subtitle')}
        />

        {step.kind !== 'done' && <EnrollProgress step={stepNumber} total={total} name={stepName} />}

        {step.kind === 'details' && (
          <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4">
            {serverError && <Banner variant="error">{serverError}</Banner>}

            <Input
              className="enroll-input"
              label={t('enroll.fields.name')}
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              error={errors.name}
              autoComplete="name"
              enterKeyHint="next"
              required
            />

            <Input
              className="enroll-input"
              label={t('enroll.fields.email')}
              type="email"
              value={form.email}
              onChange={(e) => setField('email', e.target.value)}
              error={errors.email}
              description={t('enroll.fields.emailHint')}
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              enterKeyHint="next"
              required
            />

            <Input
              className="enroll-input"
              label={t('enroll.fields.mobilePhone')}
              type="tel"
              value={form.mobilePhone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              error={errors.mobilePhone}
              description={t('enroll.fields.mobilePhoneHint')}
              autoComplete="tel"
              inputMode="tel"
              enterKeyHint="next"
              required
            />

            <TaxIdFields
              taxIdCountry={form.taxIdCountry}
              taxIdType={form.taxIdType}
              taxIdValue={form.taxIdValue}
              error={errors.taxIdValue}
              onCpfChange={handleCpfChange}
              onValueChange={(v) => setField('taxIdValue', v)}
              onCountryChange={(v) => setField('taxIdCountry', v)}
              onTypeChange={(v) => setField('taxIdType', v)}
              onNoCpfChange={(noCpf) => (noCpf ? switchToPassportMode() : switchToCpfMode())}
            />

            <Input
              className="enroll-input"
              label={t('enroll.fields.birthDate')}
              type="date"
              value={form.birthDate}
              onChange={(e) => setField('birthDate', e.target.value)}
              autoComplete="bday"
              enterKeyHint="done"
            />

            <StickyActionBar>
              <Button
                variant="primary"
                type="submit"
                loading={submitting}
                className="enroll-cta w-full"
              >
                {t('enroll.submit')}
              </Button>
            </StickyActionBar>
          </form>
        )}

        {step.kind === 'verify' && (
          <div data-testid="enroll-step-verify" className="flex flex-col gap-4">
            {verifyError && <Banner variant="error">{verifyError}</Banner>}

            <Text variant="secondary">
              {t('enroll.step2.sentTo', { email: form.email.trim() })}
            </Text>

            <form onSubmit={handleVerify} className="flex flex-col gap-4">
              <Input
                className="enroll-input enroll-code"
                label={t('enroll.step2.codeLabel')}
                placeholder={t('enroll.step2.codePlaceholder')}
                value={code}
                onChange={(e) => handleCodeChange(e.target.value)}
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]*"
                // No maxLength: a pasted "123 456" would be cut before the
                // non-digits are stripped; handleCodeChange caps it at 6.
                enterKeyHint="done"
                disabled={verifyTerminal}
                required
              />
              <StickyActionBar>
                <Button
                  variant="primary"
                  type="submit"
                  loading={verifying}
                  disabled={verifying || verifyTerminal}
                  className="enroll-cta w-full"
                >
                  {t('enroll.step2.verifyButton')}
                </Button>
              </StickyActionBar>
            </form>

            <Button
              variant="secondary"
              icon={<ArrowClockwiseIcon size={16} />}
              loading={resending}
              disabled={resending || verifyTerminal}
              onClick={() => void handleResend()}
              className="w-full"
              style={{ minHeight: 44 }}
            >
              {t('enroll.step2.resend')}
            </Button>

            {verifyTerminal && (
              <>
                <Text variant="secondary" size="sm">
                  {t('enroll.step2.cooldown')}
                </Text>
                <Button
                  variant="secondary"
                  onClick={resetToStart}
                  className="w-full"
                  style={{ minHeight: 44 }}
                >
                  {t('enroll.step2.startOver')}
                </Button>
              </>
            )}
          </div>
        )}

        {step.kind === 'photo' && <PhotoStep error={photoError} onCapture={handleCapture} />}

        {step.kind === 'confirm' && (
          <PhotoConfirm
            error={photoError}
            previewUrl={photoPreviewUrl}
            rows={[
              { label: t('enroll.fields.name'), value: form.name.trim() },
              { label: t('enroll.fields.email'), value: form.email.trim() },
            ]}
            onRetake={handleRetake}
            onSubmit={() => void handleSubmitFace()}
            submitting={submittingFace}
            canSubmit={!!photoBase64}
          />
        )}

        {step.kind === 'done' && (
          <EnrollDone
            testId="enroll-step-done"
            status={step.status}
            actionLabel={t('enroll.terminal.signIn')}
            onAction={() => navigate('/')}
          />
        )}
      </div>
    </div>
  )
}
